import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Zap, Clock, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdminEmailSequences() {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [expandedSequence, setExpandedSequence] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    triggerEvent: '',
    description: '',
    steps: [{ templateSlug: '', delayHours: 0, delayDays: 0, condition: '' }],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [seqRes, tempRes, trigRes] = await Promise.all([
        axios.get('/api/admin/email-sequences'),
        axios.get('/api/admin/email-templates'),
        axios.get('/api/admin/email-sequences/triggers'),
      ]);
      setSequences(seqRes.data.sequences || []);
      setTemplates(tempRes.data.templates || []);
      setTriggers(trigRes.data.triggers || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSequence(null);
    setFormData({
      name: '',
      triggerEvent: '',
      description: '',
      steps: [{ templateSlug: '', delayHours: 0, delayDays: 0, condition: '' }],
    });
    setShowEditor(true);
  };

  const handleEdit = (sequence) => {
    setEditingSequence(sequence);
    setFormData({
      name: sequence.name,
      triggerEvent: sequence.triggerEvent,
      description: sequence.description || '',
      steps: sequence.steps || [{ templateSlug: '', delayHours: 0, delayDays: 0, condition: '' }],
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.triggerEvent || formData.steps.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate steps have templates
    const invalidSteps = formData.steps.filter((s) => !s.templateSlug);
    if (invalidSteps.length > 0) {
      toast.error('All steps must have a template selected');
      return;
    }

    try {
      setSaving(true);
      if (editingSequence) {
        await axios.put(`/api/admin/email-sequences/${editingSequence.id}`, formData);
        toast.success('Sequence updated successfully');
      } else {
        await axios.post('/api/admin/email-sequences', formData);
        toast.success('Sequence created successfully');
      }
      setShowEditor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sequence');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sequence) => {
    if (!window.confirm(`Are you sure you want to delete "${sequence.name}"?`)) return;

    try {
      await axios.delete(`/api/admin/email-sequences/${sequence.id}`);
      toast.success('Sequence deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sequence');
    }
  };

  const handleToggleActive = async (sequence) => {
    try {
      await axios.put(`/api/admin/email-sequences/${sequence.id}`, {
        isActive: !sequence.isActive,
      });
      toast.success(`Sequence ${sequence.isActive ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sequence status');
    }
  };

  const addStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [...prev.steps, { templateSlug: '', delayHours: 0, delayDays: 0, condition: '' }],
    }));
  };

  const removeStep = (index) => {
    if (formData.steps.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      ),
    }));
  };

  const getTriggerName = (event) => {
    const trigger = triggers.find((t) => t.event === event);
    return trigger?.name || event;
  };

  const getTemplateName = (slug) => {
    const template = templates.find((t) => t.slug === slug);
    return template?.name || slug;
  };

  const formatDelay = (hours, days) => {
    if (days > 0 && hours > 0) return `${days}d ${hours}h`;
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return 'Immediately';
  };

  return (
    <div data-testid="admin-email-sequences">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Email Sequences</h1>
          <p className="text-muted-foreground">Configure automated email workflows</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-sequence-btn">
          <Plus className="w-4 h-4 mr-2" /> New Sequence
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-[var(--radius)] p-4 mb-6">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-sm">Automated Email Sequences</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Configure email sequences that automatically send based on user actions.
              Each sequence can have multiple steps with delays.
            </p>
          </div>
        </div>
      </div>

      {/* Sequences List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      ) : sequences.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-[var(--radius)]">
          <p className="text-muted-foreground mb-4">No email sequences configured</p>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" /> Create First Sequence
          </Button>
        </div>
        ) : (
          <div className="space-y-4">
            {sequences.map((sequence) => (
              <div
                key={sequence.id}
                className="bg-card border border-border rounded-[var(--radius)] overflow-hidden"
                data-testid={`sequence-${sequence.id}`}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() =>
                    setExpandedSequence(expandedSequence === sequence.id ? null : sequence.id)
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{sequence.name}</h3>
                        <Badge variant={sequence.isActive ? 'default' : 'secondary'}>
                          {sequence.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Zap className="w-4 h-4" />
                          {getTriggerName(sequence.triggerEvent)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {sequence.steps?.length || 0} step(s)
                        </span>
                      </div>
                      {sequence.description && (
                        <p className="text-sm text-muted-foreground mt-2">{sequence.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(sequence);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActive(sequence);
                        }}
                      >
                        {sequence.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(sequence);
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedSequence === sequence.id ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Steps */}
                {expandedSequence === sequence.id && sequence.steps?.length > 0 && (
                  <div className="border-t border-border bg-muted/30 p-6">
                    <h4 className="text-sm font-medium mb-4">Sequence Steps</h4>
                    <div className="space-y-3">
                      {sequence.steps.map((step, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 bg-card border border-border rounded-[var(--radius)] p-4"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{getTemplateName(step.templateSlug)}</p>
                            <p className="text-sm text-muted-foreground">
                              Send {formatDelay(step.delayHours, step.delayDays)} after trigger
                            </p>
                          </div>
                          {step.condition && (
                            <Badge variant="outline" className="text-xs">
                              {step.condition}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      {/* Sequence Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSequence ? 'Edit Sequence' : 'Create New Sequence'}
            </DialogTitle>
            <DialogDescription>
              Configure automated emails that send based on user events.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Sequence Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Welcome Series"
                  data-testid="sequence-name-input"
                />
              </div>
              <div>
                <Label htmlFor="trigger">Trigger Event *</Label>
                <Select
                  value={formData.triggerEvent}
                  onValueChange={(value) => setFormData({ ...formData, triggerEvent: value })}
                >
                  <SelectTrigger data-testid="sequence-trigger-select">
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent>
                    {triggers.map((trigger) => (
                      <SelectItem key={trigger.event} value={trigger.event}>
                        <div>
                          <span>{trigger.name}</span>
                          <span className="text-xs text-muted-foreground block">
                            {trigger.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this sequence"
              />
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Label>Sequence Steps</Label>
                <Button variant="outline" size="sm" onClick={addStep}>
                  <Plus className="w-4 h-4 mr-1" /> Add Step
                </Button>
              </div>

              <div className="space-y-4">
                {formData.steps.map((step, index) => (
                  <div
                    key={index}
                    className="border border-border rounded-[var(--radius)] p-4 bg-muted/30"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <span className="font-medium">Step {index + 1}</span>
                      {formData.steps.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(index)}
                          className="ml-auto text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-3 md:col-span-1">
                        <Label>Template *</Label>
                        <Select
                          value={step.templateSlug}
                          onValueChange={(value) => updateStep(index, 'templateSlug', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates
                              .filter((t) => t.isActive)
                              .map((template) => (
                                <SelectItem key={template.slug} value={template.slug}>
                                  {template.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Delay (Days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={step.delayDays}
                          onChange={(e) =>
                            updateStep(index, 'delayDays', parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                      <div>
                        <Label>Delay (Hours)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={step.delayHours}
                          onChange={(e) =>
                            updateStep(index, 'delayHours', parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label>Condition (Optional)</Label>
                      <Input
                        value={step.condition || ''}
                        onChange={(e) => updateStep(index, 'condition', e.target.value)}
                        placeholder='e.g., assessment_type == "fear"'
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Only send this step if condition is met
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-sequence-btn">
              {saving ? 'Saving...' : editingSequence ? 'Update Sequence' : 'Create Sequence'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
